"use client";

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import type { AccentPalette } from "@/lib/types";
import { useLocalStorageState } from "@/lib/hooks/useLocalStorageState";
import {
  ACCENT_PALETTES,
  ACCENT_STORAGE_KEY,
  DEFAULT_PALETTE_INDEX,
} from "@/lib/data/palettes";

export { ACCENT_PALETTES };

interface ThemeContextValue {
  paletteIndex: number;
  setPaletteIndex: (index: number) => void;
  accent: string;
  accentSoft: string;
  palettes: AccentPalette[];
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [paletteIndex, setPaletteIndex] = useLocalStorageState<number>(
    ACCENT_STORAGE_KEY,
    DEFAULT_PALETTE_INDEX,
  );

  const palette = ACCENT_PALETTES[paletteIndex] ?? ACCENT_PALETTES[DEFAULT_PALETTE_INDEX];
  const [accent, accentSoft] = palette;

  // Keep the CSS custom properties in sync when the user picks a palette.
  // The initial values are already applied pre-paint by the inline script in
  // `app/layout.tsx`, so this only handles live changes.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--accent", accent);
    root.style.setProperty("--accent-soft", accentSoft);
  }, [accent, accentSoft]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      paletteIndex,
      setPaletteIndex,
      accent,
      accentSoft,
      palettes: ACCENT_PALETTES,
    }),
    [paletteIndex, setPaletteIndex, accent, accentSoft],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within <ThemeProvider>");
  return ctx;
}
